import { gql } from '@apollo/client';
import { isEmpty } from 'lodash';
import { PAGE_INFO } from '../queries/global';
import { PRODUCT_FRAGMENTS } from '../queries/product';
import { TAXONOMY_FIELDS, TAXONOMY_PRODUCT_FIELDS } from '../queries/taxonomy';
import { IQueryVars } from '../types/taxonomy';
import { PRODUCT_PER_PAGE } from './product';

export const TAXONOMY_SLUG_ENUMS = {
    'product-category': 'PRODUCTCATEGORY',
    'product-tag': 'PRODUCTTAG',
    'product-type': 'PRODUCTTAXONOMYTYPE',
    favourite: 'PRODUCTTAXONOMYFAVOURITE',
    occasion: 'PRODUCTTAXONOMYOCCASION',
    'shop-by': 'PRODUCTTAXONOMYSHOPBY',
};

export const TAXONOMY_SLUG_FUNC = {
    'product-category': 'productCategory',
    'product-tag': 'productTag',
    'product-type': 'productTaxonomyType',
    favourite: 'productTaxonomyFavourite',
    occasion: 'productTaxonomyOccasion',
    'shop-by': 'productTaxonomyShopBy',
};

export const TAXONOMY_REAL_SLUG_FUNC = {
    'product-category': 'product_cat',
    'product-tag': 'product_tag',
    'product-type': 'product-type',
    favourite: 'favourite',
    occasion: 'occasion',
    'shop-by': 'shop-by',
};

export const TAXONOMY_REAL_SLUG_NAME = {
    'product-category': 'Category',
    'product-tag': 'Tag',
    'product-type': 'Type',
    favourite: 'Favourite',
    occasion: 'Occasion',
    'shop-by': 'Suitable For',
};

export const getFilterNameBySlug = (slug: string) => {
    return TAXONOMY_REAL_SLUG_NAME.hasOwnProperty(slug)
        ? TAXONOMY_REAL_SLUG_NAME[slug]
        : null;
};

export const getRealWpTaxonomySlug = (slug: string) => {
    return TAXONOMY_REAL_SLUG_FUNC.hasOwnProperty(slug)
        ? TAXONOMY_REAL_SLUG_FUNC[slug]
        : 'product_cat';
};

export const getTaxonomyEnumBySlug = (slug: string) => {
    return TAXONOMY_SLUG_ENUMS.hasOwnProperty(slug)
        ? TAXONOMY_SLUG_ENUMS[slug]
        : 'PRODUCTTAXONOMYFAVOURITE';
};

export const getTaxonomyFuncBySlug = (slug: string) => {
    return TAXONOMY_SLUG_FUNC.hasOwnProperty(slug)
        ? TAXONOMY_SLUG_FUNC[slug]
        : '';
};

export const DEFAULT_ORDERBY = ['TOTAL_SALES', 'DESC'] as const;

export const getDefaultFilterQueryVars = (
    taxonomyFilters: any,
    onSale: boolean | null = null
): IQueryVars => {
    let [defaultOrderByField, defaultOrderBySort] = DEFAULT_ORDERBY;

    return {
        offSet: 0,
        minPrice: null,
        maxPrice: null,
        orderByField: onSale === true ? 'DATE' : defaultOrderByField,
        orderBySort: onSale === true ? 'DESC' : defaultOrderBySort,
        taxonomyFilters: taxonomyFilters,
        onSale: onSale,
    };
};

export const getDefaultFilterProps = (
    taxonomyFilters: any,
    onSale: boolean | null = null
) => {
    return {
        pageNumber: 1,
        appendProducts: false,
        queryVars: getDefaultFilterQueryVars(taxonomyFilters, onSale),
        filterVars: {
            priceFilter: null,
            typeFilter: null,
            ocassionFilter: null,
            suitableForFilter: null,
            sortByState: {
                label: 'Most Popular',
                value: 'popularity',
            },
        },
    };
};

export const getTaxonomyOrderFieldAndSortOption = (slug: string) => {
    if ('popularity' === slug) {
        return DEFAULT_ORDERBY;
    } else if ('price' === slug) {
        return ['PRICE', 'ASC'] as const;
    } else if ('price-desc' === slug) {
        return ['PRICE', 'DESC'] as const;
    } else if ('on_sale' === slug) {
        return ['SALE_PRICE', 'ASC'] as const;
    } else if ('title' === slug) {
        return ['NAME', 'ASC'] as const;
    } else if ('title-desc' === slug) {
        return ['NAME', 'DESC'] as const;
    } else if ('date' === slug) {
        return ['DATE', 'DESC'] as const;
    }

    return DEFAULT_ORDERBY;
};

export const TAXONOMY_FILTER_QUERY = gql`
	query FilterTaxonomyProducts(
		$offSet: Int,
		$minPrice: Float,
		$maxPrice: Float,
		$orderByField: ProductsOrderByEnum!,
		$orderBySort: OrderEnum,
		$taxonomyFilters:[ProductTaxonomyFilterInput],
		$onSale: Boolean,
	) {
		products(
			where: {
				taxonomyFilter: {
					filters: $taxonomyFilters
				},
				offsetPagination: {
					size: ${PRODUCT_PER_PAGE},
					offset: $offSet
				},
				minPrice: $minPrice,
				maxPrice: $maxPrice,
				orderby: {
					field: $orderByField,
					order: $orderBySort
				},
				onSale: $onSale, 
				visibility: VISIBLE
			}
		) {
			nodes {${PRODUCT_FRAGMENTS}}
			${PAGE_INFO}
		}
	}
`;

export const generateTaxonomyQuery = (slug: string) => {
    const taxonomyFunc = getTaxonomyFuncBySlug(slug);
    if (isEmpty(taxonomyFunc)) {
        return taxonomyFunc;
    }

    let query = `query TAXONOMY_DATA(
		$slug: ID!
	) {
		taxonomy: ${taxonomyFunc}(id: $slug, idType: SLUG) {
			${TAXONOMY_FIELDS}
		}
	}`;

    return query;
};
